import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Mail, User, ArrowRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import XionLogo from "../components/XionLogo";
import PasswordInput from "../components/PasswordInput";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const res = await register(name, email, password);
    setLoading(false);
    if (!res.ok) { setError(res.error); return; }
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-[#F8F8F6] text-[#0E0E1A] grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 lg:p-12 order-2 lg:order-1">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-10">
            <XionLogo size={30} />
            <span className="font-display font-black text-xl tracking-tighter">XION LABS</span>
          </Link>

          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF]">/ create account</p>
          <h1 className="font-display font-black text-4xl mt-2 mb-2">Join Xion.</h1>
          <p className="text-[#5A5A72] mb-10">Track every quote and project in one place.</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">Name</label>
              <div className="mt-2 relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
                <input
                  required value={name} onChange={(e) => setName(e.target.value)}
                  data-testid="register-name-input"
                  className="w-full pl-11 pr-4 py-3.5 rounded-lg bg-white border border-black/[0.08] focus:border-[#00D4FF]/60 focus:ring-2 focus:ring-cyan-400/20 outline-none transition"
                  placeholder="Your name"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">Email</label>
              <div className="mt-2 relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="register-email-input"
                  className="w-full pl-11 pr-4 py-3.5 rounded-lg bg-white border border-black/[0.08] focus:border-[#00D4FF]/60 focus:ring-2 focus:ring-cyan-400/20 outline-none transition"
                  placeholder="you@studio.com"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-[#5A5A72]">Password</label>
              <div className="mt-2">
                <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} testId="register-password-input" minLength={6} autoComplete="new-password" placeholder="At least 6 characters" />
              </div>
            </div>
            {error && (
              <div data-testid="register-error" className="text-sm text-red-400 bg-red-500/5 border border-red-500/20 rounded-lg px-4 py-3">{error}</div>
            )}
            <button
              type="submit" disabled={loading}
              data-testid="register-submit-btn"
              className="w-full btn-primary py-3.5 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? "Creating account..." : <>Create account <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-[#5A5A72]">
            Already have an account?{" "}
            <Link to="/login" data-testid="register-to-login" className="text-[#00D4FF] hover:underline">Sign in</Link>
          </p>
        </motion.div>
      </div>

      <div className="relative hidden lg:block overflow-hidden border-l border-black/[0.06] order-1 lg:order-2">
        <img src="https://static.prod-images.emergentagent.com/jobs/41ef69e5-e4fb-4fd7-8f79-f334d2b03553/images/c405163be7cd45258c2ce74186e94458b8f81d246427cf04a115c5286a9ef0f0.png" alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-l from-[#06060C]/30 to-[#06060C]" />
        <div className="relative h-full p-12 flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <XionLogo size={36} />
            <span className="font-display font-black text-2xl tracking-tighter">XION LABS</span>
          </Link>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00D4FF] mb-3">/ get started</p>
            <h2 className="font-display font-black text-5xl tracking-tight max-w-md leading-tight">
              Your <span className="text-gradient-violet">creative ops</span><br />headquarters.
            </h2>
          </div>
        </div>
      </div>
    </div>
  );
}
